import { t } from "@lingui/macro";
import { useCallback, useEffect, useRef, useState } from "react";

import { axios } from "@/client/libs/axios";
import { getPersistentFingerprint } from "@/client/utils/fingerprint";

export type ProgressPhase = 
  | "market-scout" 
  | "architect" 
  | "formatter" 
  | "transfer";

export type ProgressEvent = {
  sessionId: string;
  phase: ProgressPhase;
  step: string;
  message: string;
  progress: number;
  isThinking?: boolean;
  thinkingContent?: string;
  timestamp: number;
};

export type MarketInsights = {
  topSkills: string[];
  salary: string | null;
  keywords: string[];
  hiddenRequirements: string[];
};

export type PhaseStatus = {
  status: "waiting" | "active" | "complete";
  startTime: number | null;
  endTime: number | null;
  elapsed: number | null;
};

export type GenerationErrorCode = "SESSION_LIMIT_EXCEEDED" | "INSUFFICIENT_TOKENS" | "GENERIC";

export type GenerationState = {
  sessionId: string | null;
  isGenerating: boolean;
  currentPhase: ProgressPhase | null;
  currentStep: string;
  currentMessage: string;
  overallProgress: number;
  thinkingContent: string | null;
  isThinking: boolean;
  error: string | null;
  errorCode: GenerationErrorCode | null;
  resumeData: any | null;
  events: ProgressEvent[];
  insights: MarketInsights | null;
  phases: Record<ProgressPhase, PhaseStatus>;
  pipelineStartTime: number | null;
};

// Phase weights reflect actual AI processing times:
// Market Scout: ~10-15s (fast)
// Architect: ~30-60s (DeepSeek reasoning)  
// Formatter: ~30-90s (DeepSeek with large prompts)
// Transfer: instant
const PHASE_WEIGHTS: Record<ProgressPhase, { start: number; weight: number }> = {
  "market-scout": { start: 0, weight: 15 },  // 0-15%
  "architect": { start: 15, weight: 40 },    // 15-55%
  "formatter": { start: 55, weight: 40 },    // 55-95%
  "transfer": { start: 95, weight: 5 },      // 95-100%
};

const PHASE_DISPLAY_NAMES: Record<ProgressPhase, string> = {
  "market-scout": "Market Analysis",
  "architect": "Resume Architect",
  "formatter": "Resume Formatter",
  "transfer": "Finalizing",
};

const INITIAL_PHASES: Record<ProgressPhase, PhaseStatus> = {
  "market-scout": { status: "waiting", startTime: null, endTime: null, elapsed: null },
  "architect": { status: "waiting", startTime: null, endTime: null, elapsed: null },
  "formatter": { status: "waiting", startTime: null, endTime: null, elapsed: null },
  "transfer": { status: "waiting", startTime: null, endTime: null, elapsed: null },
};

export function useGenerationStream() {
  const [state, setState] = useState<GenerationState>({
    sessionId: null,
    isGenerating: false,
    currentPhase: null,
    currentStep: "",
    currentMessage: "",
    overallProgress: 0,
    thinkingContent: null,
    isThinking: false,
    error: null,
    errorCode: null,
    resumeData: null,
    events: [],
    insights: null,
    phases: { ...INITIAL_PHASES },
    pipelineStartTime: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 3;

  // Calculate overall progress based on phase and phase progress
  const calculateOverallProgress = useCallback((phase: ProgressPhase, phaseProgress: number): number => {
    const phaseInfo = PHASE_WEIGHTS[phase];
    if (!phaseInfo) return 0;
    
    const phaseContribution = (phaseProgress / 100) * phaseInfo.weight;
    return Math.min(100, phaseInfo.start + phaseContribution);
  }, []);

  // Handle incoming SSE event
  const handleProgressEvent = useCallback((event: ProgressEvent) => {
    setState((prev) => {
      const now = Date.now();
      const newPhases = { ...prev.phases };

      // Track phase start
      if (event.step === "start" && newPhases[event.phase]) {
        newPhases[event.phase] = {
          status: "active", startTime: now, endTime: null, elapsed: null,
        };
      }

      // Track phase complete
      if (event.step === "complete" && newPhases[event.phase] && event.phase !== "transfer") {
        const startTime = newPhases[event.phase].startTime || now;
        newPhases[event.phase] = {
          status: "complete", startTime, endTime: now,
          elapsed: Math.round((now - startTime) / 1000),
        };
      }

      // Handle market insights
      let insights = prev.insights;
      if (event.phase === "market-scout" && event.step === "insights") {
        try { insights = JSON.parse(event.message); } catch { /* ignore */ }
      }

      // Handle final resume data
      if (event.phase === "transfer" && event.step === "complete") {
        try {
          const payload = JSON.parse(event.message);
          const resumeData = payload.resume || payload;
          const marketData = payload.marketData;

          if (marketData && resumeData) {
            resumeData.metadata = {
              ...(resumeData.metadata || {}),
              marketContext: marketData,
            };
          }

          return {
            ...prev,
            isGenerating: false,
            currentPhase: event.phase,
            currentStep: event.step,
            currentMessage: t`Resume ready!`,
            overallProgress: 100,
            isThinking: false,
            thinkingContent: null,
            resumeData,
            events: [...prev.events, event],
            insights,
            phases: newPhases,
          };
        } catch { /* not JSON */ }
      }

      if (event.step === "error") {
        return {
          ...prev,
          isGenerating: false,
          error: event.message,
          events: [...prev.events, event],
          phases: newPhases,
        };
      }

      const overallProgress = event.progress >= 0 
        ? calculateOverallProgress(event.phase, event.progress)
        : prev.overallProgress;

      return {
        ...prev,
        currentPhase: event.phase,
        currentStep: event.step,
        currentMessage: event.step === "insights" ? prev.currentMessage : event.message,
        overallProgress,
        isThinking: event.isThinking ?? false,
        thinkingContent: event.thinkingContent ?? prev.thinkingContent,
        events: [...prev.events, event],
        insights,
        phases: newPhases,
        pipelineStartTime: prev.pipelineStartTime || now,
      };
    });
  }, [calculateOverallProgress]);

  // Connect to SSE endpoint using fetch (better CORS support than EventSource)
  const connectToStream = useCallback(async (sessionId: string) => {
    // Abort any existing connection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    // Use API URL for SSE connection
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    const sseUrl = `${apiUrl}/ai/stream/progress/${sessionId}`;
    
    console.log("[SSE] Connecting via fetch to:", sseUrl);

    try {
      const response = await fetch(sseUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'text/event-stream',
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      // Log response headers for debugging
      console.log("[SSE] Response headers:");
      response.headers.forEach((value, key) => {
        console.log(`  ${key}: ${value}`);
      });

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      const decoder = new TextDecoder();
      let buffer = "";

      console.log("[SSE] Connection opened via fetch");
      reconnectAttemptsRef.current = 0;

      // Read the stream
      console.log("[SSE] Starting to read stream...");
      let readCount = 0;
      while (true) {
        console.log(`[SSE] Awaiting reader.read() #${++readCount}...`);
        const { done, value } = await reader.read();
        console.log(`[SSE] reader.read() returned: done=${done}, value.length=${value?.length || 0}`);
        
        if (done) {
          console.log("[SSE] Stream ended");
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        console.log("[SSE] Decoded chunk:", chunk.slice(0, 100) + (chunk.length > 100 ? "..." : ""));
        buffer += chunk;
        
        // Process complete SSE messages
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          console.log("[SSE] Processing line:", line.slice(0, 80));
          if (line.startsWith("data: ")) {
            const jsonStr = line.slice(6); // Remove "data: " prefix
            try {
              const data = JSON.parse(jsonStr);
              
              if (data.type === "connected") {
                console.log("[SSE] Connected to session:", data.sessionId);
                continue;
              }

              console.log("[SSE] Progress event:", data.phase, data.step);
              handleProgressEvent(data as ProgressEvent);
            } catch (parseError) {
              // Ignore parse errors for heartbeats etc
              if (jsonStr.trim()) {
                console.warn("[SSE] Failed to parse:", jsonStr);
              }
            }
          }
        }
      }
    } catch (error: any) {
      // Ignore abort errors (intentional cleanup)
      if (error?.name === 'AbortError') {
        console.log("[SSE] Connection aborted");
        return;
      }
      
      console.error("[SSE] Connection error:", error);
      
      if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current++;
        console.log(`[SSE] Reconnecting... attempt ${reconnectAttemptsRef.current}`);
        setTimeout(() => connectToStream(sessionId), 1000 * reconnectAttemptsRef.current);
      } else {
        setState((prev) => ({
          ...prev,
          error: t`Connection lost. Please try again.`,
          errorCode: "GENERIC",
          isGenerating: false,
        }));
      }
    }
  }, [handleProgressEvent]);

  // Start generation
  const startGeneration = useCallback(async (params: {
    role: string;
    location: string;
    language?: string;
    experience?: string;
    tone?: string;
    atsTargetMin?: number;
    additionalDescription?: string;
    jobUrl?: string;
  }) => {
    try {
      setState({
        sessionId: null,
        isGenerating: true,
        currentPhase: null,
        currentStep: "initializing",
        currentMessage: t`Starting AI generation...`,
        overallProgress: 0,
        thinkingContent: null,
        isThinking: false,
        error: null,
        errorCode: null,
        resumeData: null,
        events: [],
        insights: null,
        phases: { ...INITIAL_PHASES },
        pipelineStartTime: Date.now(),
      });

      // Start generation on backend
      const response = await axios.post("/ai/stream/generate", {
        ...params,
        fingerprint: getPersistentFingerprint(),
      });
      const { sessionId } = response.data;

      setState((prev) => ({ ...prev, sessionId }));

      // Connect to SSE stream
      connectToStream(sessionId);

      return sessionId;
    } catch (error: any) {
      const responseData = error?.response?.data;
      const serverError = responseData?.error;
      const errorMessage = responseData?.message || error?.message || t`Failed to start generation`;

      let errorCode: GenerationErrorCode = "GENERIC";
      if (serverError === "SESSION_LIMIT_EXCEEDED" || errorMessage?.includes("session")) {
        errorCode = "SESSION_LIMIT_EXCEEDED";
      } else if (serverError === "INSUFFICIENT_TOKENS" || errorMessage?.includes("token")) {
        errorCode = "INSUFFICIENT_TOKENS";
      }

      setState((prev) => ({
        ...prev,
        isGenerating: false,
        error: errorMessage,
        errorCode,
      }));
      throw error;
    }
  }, [connectToStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Get display name for current phase
  const currentPhaseDisplayName = state.currentPhase 
    ? PHASE_DISPLAY_NAMES[state.currentPhase] 
    : t`Initializing`;

  return {
    ...state,
    currentPhaseDisplayName,
    startGeneration,
    reset: useCallback(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setState({
        sessionId: null,
        isGenerating: false,
        currentPhase: null,
        currentStep: "",
        currentMessage: "",
        overallProgress: 0,
        thinkingContent: null,
        isThinking: false,
        error: null,
        errorCode: null,
        resumeData: null,
        events: [],
        insights: null,
        phases: { ...INITIAL_PHASES },
        pipelineStartTime: null,
      });
    }, []),
  };
}
